// Social API — feed, posts, Respect likes, comments, follows, media, avatars.
// All calls go through the shared axios instance (auth header attached there).
import api from './axiosInstance';
import config from './config';

/**
 * Resolve a stored media/avatar URL for the browser: dev returns relative
 * `/media/...` paths (serve from the API host), R2 returns absolute URLs.
 */
export const mediaUrl = (url) => (url && url.startsWith('/') ? `${config.apiUrl}${url}` : url);

const uploadFile = (path, file, onProgress) => {
  const form = new FormData();
  form.append('file', file);
  return api
    .post(path, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress
        ? (e) => e.total && onProgress(Math.round((e.loaded / e.total) * 100))
        : undefined,
    })
    .then((r) => r.data);
};

export const getFeed = (tab = 'explore', { limit = 20, offset = 0 } = {}) =>
  api.get('/social/feed', { params: { tab, limit, offset } }).then((r) => r.data);

export const getExplore = ({ limit = 20, offset = 0 } = {}) =>
  api.get('/social/explore', { params: { limit, offset } }).then((r) => r.data);

export const getPost = (postId) =>
  api.get(`/social/posts/${postId}`).then((r) => r.data);

export const createPost = (payload) =>
  api.post('/social/posts', payload).then((r) => r.data);

export const updatePost = (postId, payload) =>
  api.patch(`/social/posts/${postId}`, payload).then((r) => r.data);

export const deletePost = (postId) =>
  api.delete(`/social/posts/${postId}`);

export const uploadPostMedia = (postId, file, onProgress) =>
  uploadFile(`/social/posts/${postId}/media`, file, onProgress);

export const deletePostMedia = (postId, mediaId) =>
  api.delete(`/social/posts/${postId}/media/${mediaId}`);

export const uploadAvatar = (file) => uploadFile('/auth/avatar', file);

export const setAvatarPreset = (preset) =>
  api.put('/auth/avatar/preset', { preset }).then((r) => r.data);

export const deleteAvatar = () => api.delete('/auth/avatar');

export const getComments = (postId) =>
  api.get(`/social/posts/${postId}/comments`).then((r) => r.data);

export const likePost = (postId) =>
  api.post(`/social/posts/${postId}/like`).then((r) => r.data);

export const unlikePost = (postId) =>
  api.delete(`/social/posts/${postId}/like`).then((r) => r.data);

export const addComment = (postId, body) =>
  api.post(`/social/posts/${postId}/comments`, { body }).then((r) => r.data);

export const followUser = (userId) =>
  api.post(`/social/users/${userId}/follow`).then((r) => r.data);

export const unfollowUser = (userId) =>
  api.delete(`/social/users/${userId}/follow`).then((r) => r.data);

export const getUserProfile = (userId) =>
  api.get(`/social/users/${userId}`).then((r) => r.data);

export const getUserPosts = (userId, { limit = 20, offset = 0 } = {}) =>
  api.get(`/social/users/${userId}/posts`, { params: { limit, offset } }).then((r) => r.data);

export const getFollowers = (userId, { limit = 50, offset = 0 } = {}) =>
  api.get(`/social/users/${userId}/followers`, { params: { limit, offset } }).then((r) => r.data);

export const getFollowing = (userId, { limit = 50, offset = 0 } = {}) =>
  api.get(`/social/users/${userId}/following`, { params: { limit, offset } }).then((r) => r.data);

// ---- Session access requests ----------------------------------------------

export const requestSessionAccess = (sessionId, message) =>
  api
    .post(`/social/sessions/${sessionId}/access-request`, { message: message || null })
    .then((r) => r.data);

export const getAccessRequests = (box) =>
  api.get('/social/access-requests', { params: { box } }).then((r) => r.data);

export const approveAccessRequest = (id) =>
  api.post(`/social/access-requests/${id}/approve`).then((r) => r.data);

export const denyAccessRequest = (id) =>
  api.post(`/social/access-requests/${id}/deny`).then((r) => r.data);
